import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { usePostApiLogin } from '@/api/generated/public/public'
import { useAuthStore } from '@/store/auth.store'
import { isServerUnreachable } from '@/lib/apiError'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card'
import { Eye, EyeOff, Loader } from 'lucide-react'
import { Separator } from '@/components/shadcn/separator'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { ThemeToggle } from '@/components/ThemeToggle'
import { WallpaperBackground } from '@/pages/login/components/WallpaperBackground'
import { resolveLoginDestination } from '@/pages/login/loginDestination'

export default function LoginPage() {
  const { t } = useTranslation('login')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const setAuth = useAuthStore((s) => s.setAuth)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { mutate: login, isPending } = usePostApiLogin({
    mutation: {
      onSuccess: (response) => {
        setAuth(response.data)
        const destination = resolveLoginDestination(searchParams.get('returnUrl'), response.data.features)
        navigate(destination, { replace: true })
      },
      onError: (error) => {
        // „E-Mail/Passwort falsch" nur bei einer echten Auth-Antwort zeigen; ist das Backend
        // nicht erreichbar, ist die Aussage über die Zugangsdaten schlicht unbekannt.
        setError(isServerUnreachable(error) ? t('serverUnreachable') : t('loginPage.loginError'))
      },
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    login({ data: { email, password } })
  }

  return (
    <WallpaperBackground>
      <Card className="w-full max-w-xl">
        <div className="grid grid-cols-[65%_35%] gap-0">
          <div>
        <CardHeader>
          <div className="flex flex-row items-start justify-between">
            <CardTitle className="text-2xl">{t('loginPage.title')}</CardTitle>
            <div className="flex items-center gap-1">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
          </div>
          <CardDescription>{t('loginPage.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4 mt-5">
            <div className="space-y-2">
              <Label htmlFor="email">{t('loginPage.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="user@example.com"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('loginPage.password')}</Label>
                <Link to="/reset_password" className="text-sm font-medium text-primary hover:underline">
                  {t('loginPage.forgotPassword')}
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <Button type="submit" disabled={isPending} className="w-full">
              {isPending ? <Loader/> : t('loginPage.loginButton')}
            </Button>
          </form>


          <Separator className='mt-6'/>
          <div className="mt-6 text-center text-sm flex justify-between">
            <span className="text-muted-foreground">{t('loginPage.noAccount')} </span>
            <Link to="/register" className="font-medium text-primary hover:underline">
              {t('loginPage.registerNow')}
            </Link>
          </div>
        </CardContent>
        </div>
        <div className="flex items-center justify-center">
           <img
              src="/logos/Bonisoft__signet_iceblue.svg"
              alt="Bonisoft"
              className="hidden md:block w-full h-auto object-contain -ml-7"
            />
        </div>
      </div>
      </Card>
    </WallpaperBackground>
  )
}
